import { MODE_ENV } from "@/services/constants";
import { AuthRole } from "@/store/AuthStore";

const MODE = import.meta.env.VITE_MODE_APP;

const RESTAURANT = MODE === MODE_ENV.PARTNER ? "" : "/restaurant";

/** URL prefix for admin routes (empty in admin-only builds). */
const ADMIN_PREFIX = MODE === MODE_ENV.ADMIN ? "" : "/admin";

/** Navigable admin entry point — never an empty string. */
const ADMIN_ROOT = MODE === MODE_ENV.ADMIN ? "/" : "/admin";

export const isAdminOnlyMode = MODE === MODE_ENV.ADMIN;

export const BASE = {
  RESET_PASSWORD: "/reset-password",
  ONBOARDING: "/onboarding",
  CREATE_BRAND: (id: string) => `/onboarding/brand/${id}`,
  CREATE_BRANCH: (id: string) => `/onboarding/brand/${id}/branch`,
  VERIFY_PHONE: "/verify-phone",
  RESTAURANT: RESTAURANT,
  ADMIN: ADMIN_PREFIX,
  SETTINGS: "profile-settings",
  SYSTEM: "system",
  TEAM: "all-admin-team",
  ACC_MANAGEMNT: "account-management",
};

export const LIVE_TRANSACTIONS_PATTERN = `${BASE.RESTAURANT}/:level/:id/live-transaction`;

export const GROUPS = {
  OVERVIEW: "overview",
  FUNDING: "funding",
  TRANSACTIONS: "transactions",
  ORDER: (id: string) => `transactions/${id}`,
  CREDIT_BALANCE: "credit-balance",
  USERS: "users",
  DINERS: "diners",
  ONE_DINER: (id: string) => `diners/${id}`,
};

export const BRANDS = {
  OVERVIEW: "overview",
  RESERVATIONS: "reservations",
  TRANSACTIONS: "transactions",
  ORDER: (id: string) => `transactions/${id}`,
  FUNDING: "funding",
  USERS: "users",
  DINERS: "diners",
  ONE_DINER: (id: string) => `diners/${id}`,
  MENU: "menu",
  NEW_DISH: "menu/new-dish",
  SETTINGS: "settings",
};

export const BRANCHES = {
  OVERVIEW: "overview",
  RESERVATIONS: "reservations",
  TRANSACTIONS: "transactions",
  ORDER: (id: string) => `transactions/${id}`,
  FUNDING: "funding",
  REVIEWS: "reviews",
  USERS: "users",
  DINERS: "diners",
  ONE_DINER: (id: string) => `diners/${id}`,
  MENU: "menu",
  SETTINGS: "settings",
  NEW_DISH: "menu/new-dish",
};

const BRANCH_STEP = {
  DETAILS: "details",
  HOURS: "business-hours",
  VIBES: "vibes",
  AMENITIES: "amenities",
  MEDIA: "media",
};

const BRAND_STEP = {
  DETAILS: "details",
  MENU: "menu",
  CONTACTS: "contacts",
  BRANCHES: "branches",
  NEW_DISH: "menu/new-dish",
};

export const ADMINS = {
  OVERVIEW: "overview",
  ADMIN_TEAM: `web-config/${BASE.TEAM}`,
  PROFILE_SETTINGS: BASE.SETTINGS,
  APPLICATION: (id: string) => `application/${id}`,

  USERS_PRIVATE: (id: string) => `${BASE.TEAM}/${id}`,
  ACCOUNT: BASE.ACC_MANAGEMNT,
  ACCOUNT_NEW_LEGAL_ENTITY: `${BASE.ACC_MANAGEMNT}/new-legal-entity`,
  ACCOUNT_LEGAL_ENTITY: (id: string) =>
    `${BASE.ACC_MANAGEMNT}/legal-entity/${id}`,
  ACCOUNT_NEW_GROUP: `${BASE.ACC_MANAGEMNT}/new-group`,

  ACCOUNT_GROUP_DETAILS: (id: string) => `${BASE.ACC_MANAGEMNT}/group/${id}`,
  ACCOUNT_GROUP_NEW_FUNDING: (id: string) =>
    `${BASE.ACC_MANAGEMNT}/group/${id}/new-funding`,
  ACCOUNT_BRAND_DETAILS: (id: string) => `${BASE.ACC_MANAGEMNT}/brand/${id}`,
  ACCOUNT_BRANCH: (id: string) => `${BASE.ACC_MANAGEMNT}/branch/${id}`,

  ACCOUNT_BRANCH_TRANSACTIONS_DETAILS: (id: string, transactionId: string) =>
    `${BASE.ACC_MANAGEMNT}/branch/${id}/transaction/${transactionId}`,

  DINERS: "diners",
  DINERS_USER: (id: string) => `diners/${id}/user`,
  DINERS_USER_TRANSACTIONS: (id: string, transactionId: string) =>
    `diners/${id}/transaction/${transactionId}`,
  REPORTING: "reporting/dashboards",
  CREDIT_BALANCE_REPORTING: "reporting/credit-balance",
  AUDIT: "reporting/audit-log",
  SYSTEMS_APP_CONTROL: "mobile-config/app-control",
  SYSTEMS_TRANSACTIONS: "reporting/transactions",
  SYSTEMS_TOP_UPS: "reporting/top-ups",
  SYSTEMS_TRANSACTIONS_DETAILS: (transactionId: string) =>
    `transactions/${transactionId}`,
  SYSTEMS_NEW_COLLECTION: `${BASE.SYSTEM}/app-control/new-collection`,
  SYSTEMS_COLLECTION: (id: string) =>
    `${BASE.SYSTEM}/app-control/collection/${id}`,
  SYSTEMS_PROMOTION: "integrations/promotion-engine",
  SYSTEMS_MARKETING: "integrations/marketing-campaigns",
  SYSTEM_TAGS: "mobile-config/tags",
  USERS_ROLES: "web-config/portal-users-roles",
  ALL_PORTAL_USERS: "web-config/all-portal-users",
  PORTAL_USER_PAGE: (id: string) => `web-config/all-portal-users/${id}`,
  ADMIN_USERS_ROLES: "web-config/admin-team-roles",
  SYSTEM_BUSINESS_RULES: "mobile-config/business-rules",
  SYSTEM_PAYMENTS: "reporting/settlements",
  SYSTEM_EMAILS: "integrations/emails",

  EDIT_BRANCH_UNDER_NEW_GROUP_DETAILS: (branchId: string) =>
    `${BASE.ACC_MANAGEMNT}/new-group/new-brand/branch/${branchId}/${BRANCH_STEP.DETAILS}`,
};

export const PATHS = {
  HOME: "/",
  LOGIN_ERROR: "/login-error",

  FOODICS: {
    INSTALL: "/foodics/install",
    /** Foodics OAuth redirect URI alias — same UI as INSTALL */
    OAUTH_CALLBACK: "/api/foodics/oauth/callback",
  },

  ADMIN: {
    ROOT: ADMIN_ROOT,
    LOGIN: `${BASE.ADMIN}/login`,
    FORGOT_PASSWORD: `${BASE.ADMIN}/forgot-password`,
    PASSWORD_LINK_SEND: `${BASE.ADMIN}/password-link-send`,
    RESET_PASSWORD: (token: string) =>
      `${BASE.ADMIN}${BASE.RESET_PASSWORD}/${token}`,
    RESET_PASSWORD_ERROR: `${BASE.ADMIN}${BASE.RESET_PASSWORD}/error`,

    WELCOME_TO_SPICE: (token: string) => `${BASE.ADMIN}/invitation/${token}`,
    SUCCESSFUL_ACTIVATION: `${BASE.ADMIN}/invitation/success`,
    INVITATION_EXPIRED: `${BASE.ADMIN}/invitation/expired`,

    OVERVIEW: `${BASE.ADMIN}/${ADMINS.OVERVIEW}`,
    PROFILE_SETTINGS: `${BASE.ADMIN}/${ADMINS.PROFILE_SETTINGS}`,
    APPLICATION: (id: string) => `${BASE.ADMIN}/${ADMINS.APPLICATION(id)}`,

    ACCOUNT_NEW_LEGAL_ENTITY: `${BASE.ADMIN}/${ADMINS.ACCOUNT_NEW_LEGAL_ENTITY}`,
    ACCOUNT_LEGAL_ENTITY: (id: string) =>
      `${BASE.ADMIN}/${ADMINS.ACCOUNT_LEGAL_ENTITY(id)}`,

    ADMIN_TEAM: `${BASE.ADMIN}/${ADMINS.ADMIN_TEAM}`,
    USERS_PRIVATE: (id: string) => `${BASE.ADMIN}/${ADMINS.USERS_PRIVATE(id)}`,
    ACCOUNT: `${BASE.ADMIN}/${ADMINS.ACCOUNT}`,
    ACCOUNT_NEW_GROUP: `${BASE.ADMIN}/${ADMINS.ACCOUNT_NEW_GROUP}`,
    ACCOUNT_GROUP_DETAILS: (id: string) =>
      `${BASE.ADMIN}/${ADMINS.ACCOUNT_GROUP_DETAILS(id)}`,
    ACCOUNT_GROUP_NEW_FUNDING: (id: string) =>
      `${BASE.ADMIN}/${ADMINS.ACCOUNT_GROUP_NEW_FUNDING(id)}`,

    ACCOUNT_BRAND_DETAILS: (id: string) =>
      `${BASE.ADMIN}/${ADMINS.ACCOUNT_BRAND_DETAILS(id)}`,
    ACCOUNT_BRANCH: (id: string) =>
      `${BASE.ADMIN}/${ADMINS.ACCOUNT_BRANCH(id)}`,
    PORTAL_USER_PAGE: (id: string) =>
      `${BASE.ADMIN}/${ADMINS.PORTAL_USER_PAGE(id)}`,

    BRAND_WIZARD_DETAILS: (brandId?: string, groupId?: string) => {
      const groupIdParam = groupId ? `?groupId=${groupId}` : "";
      const url = brandId
        ? `${BASE.ADMIN}/${BASE.ACC_MANAGEMNT}/brands/wizard/${brandId}/${BRAND_STEP.DETAILS}${groupIdParam}`
        : `${BASE.ADMIN}/${BASE.ACC_MANAGEMNT}/brands/wizard/${BRAND_STEP.DETAILS}${groupIdParam}`;
      return url;
    },
    BRAND_WIZARD_MENU: (brandId?: string) =>
      brandId
        ? `${BASE.ADMIN}/${BASE.ACC_MANAGEMNT}/brands/wizard/${brandId}/${BRAND_STEP.MENU}`
        : `${BASE.ADMIN}/${BASE.ACC_MANAGEMNT}/brands/wizard/${BRAND_STEP.MENU}`,
    BRAND_WIZARD_CONTACTS: (brandId?: string) =>
      brandId
        ? `${BASE.ADMIN}/${BASE.ACC_MANAGEMNT}/brands/wizard/${brandId}/${BRAND_STEP.CONTACTS}`
        : `${BASE.ADMIN}/${BASE.ACC_MANAGEMNT}/brands/wizard/${BRAND_STEP.CONTACTS}`,
    BRAND_WIZARD_BRANCHES: (brandId?: string) =>
      brandId
        ? `${BASE.ADMIN}/${BASE.ACC_MANAGEMNT}/brands/wizard/${brandId}/${BRAND_STEP.BRANCHES}`
        : `${BASE.ADMIN}/${BASE.ACC_MANAGEMNT}/brands/wizard/${BRAND_STEP.BRANCHES}`,
    BRAND_WIZARD_DISH: (brandId?: string) =>
      brandId
        ? `${BASE.ADMIN}/${BASE.ACC_MANAGEMNT}/brands/wizard/${brandId}/${BRAND_STEP.NEW_DISH}`
        : `${BASE.ADMIN}/${BASE.ACC_MANAGEMNT}/brands/wizard/${BRAND_STEP.NEW_DISH}`,

    // Unified branch wizard route - /branches/wizard/{branchId}?brandId={brandId}
    // branchId is optional (if exists = update, if not = create)
    // brandId is optional query parameter (if exists = fixed, if not = selectable)
    BRANCH_WIZARD_DETAILS: (branchId?: string, brandId?: string) =>
      branchId
        ? `${BASE.ADMIN}/${BASE.ACC_MANAGEMNT}/branches/wizard/${branchId}/${BRANCH_STEP.DETAILS}${brandId ? `?brandId=${brandId}` : ""}`
        : `${BASE.ADMIN}/${BASE.ACC_MANAGEMNT}/branches/wizard/${BRANCH_STEP.DETAILS}${brandId ? `?brandId=${brandId}` : ""}`,
    BRANCH_WIZARD_HOURS: (branchId?: string) =>
      branchId
        ? `${BASE.ADMIN}/${BASE.ACC_MANAGEMNT}/branches/wizard/${branchId}/${BRANCH_STEP.HOURS}`
        : `${BASE.ADMIN}/${BASE.ACC_MANAGEMNT}/branches/wizard/${BRANCH_STEP.HOURS}`,
    BRANCH_WIZARD_VIBES: (branchId?: string) =>
      branchId
        ? `${BASE.ADMIN}/${BASE.ACC_MANAGEMNT}/branches/wizard/${branchId}/${BRANCH_STEP.VIBES}`
        : `${BASE.ADMIN}/${BASE.ACC_MANAGEMNT}/branches/wizard/${BRANCH_STEP.VIBES}`,
    BRANCH_WIZARD_AMENITIES: (branchId?: string) =>
      branchId
        ? `${BASE.ADMIN}/${BASE.ACC_MANAGEMNT}/branches/wizard/${branchId}/${BRANCH_STEP.AMENITIES}`
        : `${BASE.ADMIN}/${BASE.ACC_MANAGEMNT}/branches/wizard/${BRANCH_STEP.AMENITIES}`,
    BRANCH_WIZARD_MEDIA: (branchId?: string) =>
      branchId
        ? `${BASE.ADMIN}/${BASE.ACC_MANAGEMNT}/branches/wizard/${branchId}/${BRANCH_STEP.MEDIA}`
        : `${BASE.ADMIN}/${BASE.ACC_MANAGEMNT}/branches/wizard/${BRANCH_STEP.MEDIA}`,

    ACCOUNT_BRANCH_TRANSACTIONS_DETAILS: (id: string, transactionId: string) =>
      `${BASE.ADMIN}/${ADMINS.ACCOUNT_BRANCH_TRANSACTIONS_DETAILS(id, transactionId)}`,

    DINERS: `${BASE.ADMIN}/${ADMINS.DINERS}`,
    DINERS_USER: (id: string) => `${BASE.ADMIN}/${ADMINS.DINERS_USER(id)}`,
    DINERS_USER_TRANSACTIONS: (id: string, transactionId: string) =>
      `${BASE.ADMIN}/${ADMINS.DINERS_USER_TRANSACTIONS(id, transactionId)}`,
    REPORTING: `${BASE.ADMIN}/${ADMINS.REPORTING}`,
    CREDIT_BALANCE_REPORTING: `${BASE.ADMIN}/${ADMINS.CREDIT_BALANCE_REPORTING}`,
    AUDIT: `${BASE.ADMIN}/${ADMINS.AUDIT}`,

    SYSTEMS_APP_CONTROL: `${BASE.ADMIN}/${ADMINS.SYSTEMS_APP_CONTROL}`,
    SYSTEMS_TRANSACTIONS: `${BASE.ADMIN}/${ADMINS.SYSTEMS_TRANSACTIONS}`,
    SYSTEMS_TOP_UPS: `${BASE.ADMIN}/${ADMINS.SYSTEMS_TOP_UPS}`,
    SYSTEMS_TRANSACTIONS_DETAILS: (transactionId: string) =>
      `${BASE.ADMIN}/${ADMINS.SYSTEMS_TRANSACTIONS_DETAILS(transactionId)}`,
    SYSTEMS_NEW_COLLECTION: `${BASE.ADMIN}/${ADMINS.SYSTEMS_NEW_COLLECTION}`,
    SYSTEMS_COLLECTION: (id: string) =>
      `${BASE.ADMIN}/${ADMINS.SYSTEMS_COLLECTION(id)}`,
    SYSTEMS_PROMOTION: `${BASE.ADMIN}/${ADMINS.SYSTEMS_PROMOTION}`,
    SYSTEMS_MARKETING: `${BASE.ADMIN}/${ADMINS.SYSTEMS_MARKETING}`,
    SYSTEM_TAGS: `${BASE.ADMIN}/${ADMINS.SYSTEM_TAGS}`,
    USERS_ROLES: `${BASE.ADMIN}/${ADMINS.USERS_ROLES}`,
    ALL_PORTAL_USERS: `${BASE.ADMIN}/${ADMINS.ALL_PORTAL_USERS}`,
    ADMIN_USERS_ROLES: `${BASE.ADMIN}/${ADMINS.ADMIN_USERS_ROLES}`,
    SYSTEM_BUSINESS_RULES: `${BASE.ADMIN}/${ADMINS.SYSTEM_BUSINESS_RULES}`,
    SYSTEM_PAYMENTS: `${BASE.ADMIN}/${ADMINS.SYSTEM_PAYMENTS}`,
    SYSTEM_EMAILS: `${BASE.ADMIN}/${ADMINS.SYSTEM_EMAILS}`,

    EDIT_BRANCH_UNDER_NEW_GROUP_DETAILS: (branchId: string) =>
      `${BASE.ADMIN}/${ADMINS.EDIT_BRANCH_UNDER_NEW_GROUP_DETAILS(branchId)}`,
  },

  RESTAURANT: {
    ROOT: BASE.RESTAURANT,
    LOGIN: `${BASE.RESTAURANT}/login`,
    FORGOT_PASSWORD: `${BASE.RESTAURANT}/forgot-password`,
    WELCOME_TO_SPICE: (token: string) =>
      `${BASE.RESTAURANT}/invitation/${token}`,
    SUCCESSFUL_ACTIVATION: `${BASE.RESTAURANT}/invitation/success`,
    INVITATION_EXPIRED: `${BASE.RESTAURANT}/invitation/expired`,

    GROUP: {
      ROOT: `${BASE.RESTAURANT}/group`,
      OVERVIEW: `${BASE.RESTAURANT}/group/${GROUPS.OVERVIEW}`,
      TRANSACTIONS: `${BASE.RESTAURANT}/group/${GROUPS.TRANSACTIONS}`,
      LIVE_TRANSACTIONS: (groupId: string) =>
        `${BASE.RESTAURANT}/group/${groupId}/live-transaction`,
      ORDER: (id: string) => `${BASE.RESTAURANT}/group/${GROUPS.ORDER(id)}`,
      CREDIT_BALANCE: `${BASE.RESTAURANT}/group/${GROUPS.CREDIT_BALANCE}`,
      FUNDING: `${BASE.RESTAURANT}/group/${GROUPS.FUNDING}`,
      USERS: `${BASE.RESTAURANT}/group/${GROUPS.USERS}`,
      DINERS: `${BASE.RESTAURANT}/group/${GROUPS.DINERS}`,
      ONE_DINER: (id: string) =>
        `${BASE.RESTAURANT}/group/${GROUPS.ONE_DINER(id)}`,
      PROFILE_SETTINGS: `${BASE.RESTAURANT}/group/${BASE.SETTINGS}`,

      NEW_BRAND_DETAILS: `${BASE.RESTAURANT}/group/brand/${BRAND_STEP.DETAILS}`,
      NEW_BRAND_MENU: `${BASE.RESTAURANT}/group/brand/${BRAND_STEP.MENU}`,
      NEW_BRAND_CONTACTS: `${BASE.RESTAURANT}/group/brand/${BRAND_STEP.CONTACTS}`,
      NEW_BRAND_BRANCHES: `${BASE.RESTAURANT}/group/brand/${BRAND_STEP.BRANCHES}`,
      NEW_BRAND_DISH: `${BASE.RESTAURANT}/group/brand/${BRAND_STEP.NEW_DISH}`,

      NEW_BRANCH_DETAILS: `${BASE.RESTAURANT}/group/brand/branch/${BRANCH_STEP.DETAILS}`,
      NEW_BRANCH_HOURS: `${BASE.RESTAURANT}/group/brand/branch/${BRANCH_STEP.HOURS}`,
      NEW_BRANCH_VIBES: `${BASE.RESTAURANT}/group/brand/branch/${BRANCH_STEP.VIBES}`,
      NEW_BRANCH_AMENITIES: `${BASE.RESTAURANT}/group/brand/branch/${BRANCH_STEP.AMENITIES}`,
      NEW_BRANCH_MEDIA: `${BASE.RESTAURANT}/group/brand/branch/${BRANCH_STEP.MEDIA}`,
    },

    BRAND: {
      ROOT: `${BASE.RESTAURANT}/brand`,
      OVERVIEW: `${BASE.RESTAURANT}/brand/${BRANDS.OVERVIEW}`,
      RESERVATIONS: `${BASE.RESTAURANT}/brand/${BRANDS.RESERVATIONS}`,
      TRANSACTIONS: `${BASE.RESTAURANT}/brand/${BRANDS.TRANSACTIONS}`,
      LIVE_TRANSACTIONS: (brandId: string) =>
        `${BASE.RESTAURANT}/brand/${brandId}/live-transaction`,
      ORDER: (id: string) => `${BASE.RESTAURANT}/brand/${BRANDS.ORDER(id)}`,
      FUNDING: `${BASE.RESTAURANT}/brand/${BRANDS.FUNDING}`,
      USERS: `${BASE.RESTAURANT}/brand/${BRANDS.USERS}`,
      DINERS: `${BASE.RESTAURANT}/brand/${BRANDS.DINERS}`,
      ONE_DINER: (id: string) =>
        `${BASE.RESTAURANT}/brand/${BRANDS.ONE_DINER(id)}`,
      MENU: `${BASE.RESTAURANT}/brand/${BRANDS.MENU}`,
      NEW_DISH: `${BASE.RESTAURANT}/brand/${BRANDS.NEW_DISH}`,
      SETTINGS: `${BASE.RESTAURANT}/brand/${BRANDS.SETTINGS}`,
      PROFILE_SETTINGS: `${BASE.RESTAURANT}/brand/${BASE.SETTINGS}`,

      NEW_BRANCH_DETAILS: `${BASE.RESTAURANT}/brand/branch/${BRANCH_STEP.DETAILS}`,
      NEW_BRANCH_HOURS: `${BASE.RESTAURANT}/brand/branch/${BRANCH_STEP.HOURS}`,
      NEW_BRANCH_VIBES: `${BASE.RESTAURANT}/brand/branch/${BRANCH_STEP.VIBES}`,
      NEW_BRANCH_AMENITIES: `${BASE.RESTAURANT}/brand/branch/${BRANCH_STEP.AMENITIES}`,
      NEW_BRANCH_MEDIA: `${BASE.RESTAURANT}/brand/branch/${BRANCH_STEP.MEDIA}`,
    },

    BRANCH: {
      ROOT: `${BASE.RESTAURANT}/branch`,
      OVERVIEW: `${BASE.RESTAURANT}/branch/${BRANCHES.OVERVIEW}`,
      RESERVATIONS: `${BASE.RESTAURANT}/branch/${BRANCHES.RESERVATIONS}`,
      TRANSACTIONS: `${BASE.RESTAURANT}/branch/${BRANCHES.TRANSACTIONS}`,
      LIVE_TRANSACTIONS: (branchId: string) =>
        `${BASE.RESTAURANT}/branch/${branchId}/live-transaction`,
      ORDER: (id: string) => `${BASE.RESTAURANT}/branch/${BRANCHES.ORDER(id)}`,
      FUNDING: `${BASE.RESTAURANT}/branch/${BRANCHES.FUNDING}`,
      REVIEWS: `${BASE.RESTAURANT}/branch/${BRANCHES.REVIEWS}`,
      USERS: `${BASE.RESTAURANT}/branch/${BRANCHES.USERS}`,
      DINERS: `${BASE.RESTAURANT}/branch/${BRANCHES.DINERS}`,
      ONE_DINER: (id: string) =>
        `${BASE.RESTAURANT}/branch/${BRANCHES.ONE_DINER(id)}`,
      MENU: `${BASE.RESTAURANT}/branch/${BRANCHES.MENU}`,
      NEW_DISH: `${BASE.RESTAURANT}/branch/${BRANCHES.NEW_DISH}`,
      SETTINGS: `${BASE.RESTAURANT}/branch/${BRANCHES.SETTINGS}`,
      PROFILE_SETTINGS: `${BASE.RESTAURANT}/branch/${BASE.SETTINGS}`,
      BUSINESS_MANAGEMENT_DETAILS: `${BASE.RESTAURANT}/branch/business-management/${BRANCH_STEP.DETAILS}`,
      BUSINESS_MANAGEMENT_HOURS: `${BASE.RESTAURANT}/branch/business-management/${BRANCH_STEP.HOURS}`,
      BUSINESS_MANAGEMENT_VIBES: `${BASE.RESTAURANT}/branch/business-management/${BRANCH_STEP.VIBES}`,
      BUSINESS_MANAGEMENT_AMENITIES: `${BASE.RESTAURANT}/branch/business-management/${BRANCH_STEP.AMENITIES}`,
      BUSINESS_MANAGEMENT_MEDIA: `${BASE.RESTAURANT}/branch/business-management/${BRANCH_STEP.MEDIA}`,
    },
  },

  ONBOARDING: {
    ROOT: BASE.ONBOARDING,
    PROFILE: `${BASE.ONBOARDING}/profile`,
    GROUP_INFO: `${BASE.ONBOARDING}/group-information`,
    PREAPPROVED: `${BASE.ONBOARDING}/preapproved`,
    RESTAURANT_GROUP_INFO: `${BASE.ONBOARDING}/restaurant-group-information`,
    WAITING: `${BASE.ONBOARDING}/waiting`,
    SAVED: `${BASE.ONBOARDING}/restaurant-saved`,

    CREATE_BRAND: {
      ROOT: BASE.CREATE_BRAND,
      DETAILS: (id: string) => `${BASE.CREATE_BRAND(id)}/${BRAND_STEP.DETAILS}`,
      MENU: (id: string) => `${BASE.CREATE_BRAND(id)}/${BRAND_STEP.MENU}`,
      CONTACTS: (id: string) =>
        `${BASE.CREATE_BRAND(id)}/${BRAND_STEP.CONTACTS}`,
      BRANCHES: (id: string) =>
        `${BASE.CREATE_BRAND(id)}/${BRAND_STEP.BRANCHES}`,
      NEW_DISH: (id: string) =>
        `${BASE.CREATE_BRAND(id)}/${BRAND_STEP.NEW_DISH}`,
    },

    CREATE_BRANCH: {
      ROOT: BASE.CREATE_BRANCH,
      DETAILS: (id: string) =>
        `${BASE.CREATE_BRANCH(id)}/${BRANCH_STEP.DETAILS}`,
      HOURS: (id: string) => `${BASE.CREATE_BRANCH(id)}/${BRANCH_STEP.HOURS}`,
      VIBES: (id: string) => `${BASE.CREATE_BRANCH(id)}/${BRANCH_STEP.VIBES}`,
      AMENITIES: (id: string) =>
        `${BASE.CREATE_BRANCH(id)}/${BRANCH_STEP.AMENITIES}`,
      MEDIA: (id: string) => `${BASE.CREATE_BRANCH(id)}/${BRANCH_STEP.MEDIA}`,
    },
  },

  VERIFY_PHONE: {
    ROOT: BASE.VERIFY_PHONE,
    SUCCESS: `${BASE.VERIFY_PHONE}/success`,
    ERROR: `${BASE.VERIFY_PHONE}/error`,
  },

  NOT_FOUND: "*",
};

/** Resolves portal type from env and, in dev mode, from the current path. */
export const getAppUserType = (pathname: string): AuthRole => {
  if (MODE === MODE_ENV.ADMIN) return AuthRole.ADMIN;
  if (MODE === MODE_ENV.PARTNER) return AuthRole.RESTAURANT;

  if (pathname === ADMIN_ROOT || pathname.startsWith(`${ADMIN_ROOT}/`)) {
    return AuthRole.ADMIN;
  }

  return AuthRole.RESTAURANT;
};

export const getLoginPathForUserType = (userType: AuthRole): string =>
  userType === AuthRole.ADMIN ? PATHS.ADMIN.LOGIN : PATHS.RESTAURANT.LOGIN;

const resolvePathname = (pathname?: string): string =>
  pathname ?? (typeof window !== "undefined" ? window.location.pathname : "");

/** Login page for the current app context (env + path in dev mode). */
export const getAppLoginPath = (pathname?: string): string => {
  if (MODE === MODE_ENV.ADMIN) return PATHS.ADMIN.LOGIN;
  if (MODE === MODE_ENV.PARTNER) return PATHS.RESTAURANT.LOGIN;
  return getLoginPathForUserType(getAppUserType(resolvePathname(pathname)));
};

/** Home/dashboard path for the current app context (env + path in dev mode). */
export const getAppHomePath = (pathname?: string): string => {
  if (MODE === MODE_ENV.ADMIN) return PATHS.ADMIN.ROOT;
  if (MODE === MODE_ENV.PARTNER) return PATHS.RESTAURANT.ROOT || "/";
  return getAppUserType(resolvePathname(pathname)) === AuthRole.ADMIN
    ? PATHS.ADMIN.ROOT
    : PATHS.RESTAURANT.ROOT || "/";
};

/** Login page to use after logout or session expiry. */
export const getLogoutLoginPath = (
  isAuthenticatedAdmin: boolean,
  pathname?: string
): string => {
  if (isAuthenticatedAdmin) return PATHS.ADMIN.LOGIN;
  return getAppLoginPath(pathname);
};

export const MENU_HASH = {
  DETAILS: {
    HASH: "#details",
    ID: "details",
  },
  FLAGS: {
    HASH: "#flags",
    ID: "flags",
  },
  CONTEXT: {
    HASH: "#context",
    ID: "context",
  },
  TASTE: {
    HASH: "#taste",
    ID: "taste",
  },
  DIET: {
    HASH: "#diet",
    ID: "diet",
  },
  NUTRITION: {
    HASH: "#nutrition",
    ID: "nutrition",
  },
  PHOTOS: {
    HASH: "#photo",
    ID: "photo",
  },
};
